import * as stylex from '@stylexjs/stylex';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createFileRoute,
  notFound,
  redirect,
  useLocation,
  useNavigate,
} from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { useContext, useState } from 'react';
import { ResizableTableContainer, Table, TableLayout, Virtualizer } from 'react-aria-components';
import { pick as rPick } from 'remeda';
import {
  boolean as vBoolean,
  fallback as vFallback,
  nullish as vNullish,
  number as vNumber,
  object as vObject,
  parse as vParse,
  pipe as vPipe,
  string as vString,
  transform as vTransform,
} from 'valibot';

import { SecondaryActionsIconButton } from '@/components/secondary-actions-icon-button.component';
import { TableRowSecondaryActions } from '@/components/table-row-secondary-actions.component';
import { CampaignRequestEditStatusEnum } from '@/libs/openapi';
import {
  Breadcrumb,
  Breadcrumbs,
  Cell,
  ChevronRightStrokeIcon,
  CloseStrokeIcon,
  Column,
  EditStrokeIcon,
  EmptyState,
  Heading,
  MenuItem,
  Row,
  Skeleton,
  type SkeletonRow,
  TableBody,
  TableHeader,
  TableOverlay,
  Text,
  TOAST_TIMEOUT,
  useLocalStorage,
  useMediaQuery,
} from '@/libs/serenity-ui';
import { breakpoints } from '@/libs/serenity-ui/constants.stylex';
import { RouterLink } from '@/libs/tanstack-router';
import { campaignEditRequestMutations, campaignEditRequestQueries } from '@/resources/campaign-edit-request/campaign-edit-request.api';
import { campaignEditRequestPolicies } from '@/resources/campaign-edit-request/campaign-edit-request.policies';
import {
  campaignEditRequestTableOptions,
  expandChevronStyles,
  isSubRow,
  type EditRequestFlatRow,
  type EditRequestParentRow,
  type EditRequestSubRow,
} from '@/resources/campaign-edit-request/campaign-edit-request.table';
import { campaignRequestQueries } from '@/resources/campaign-request/campaign-request.api';
import { Route as ListRoute } from '@/routes/_main/_list';
import { NewRecordContext } from '@/routes/_main/_list/-components/new-records-provider.component';
import { Pagination } from '@/routes/_main/_list/-components/pagination.component';
import { Resource } from '@/types/resource.type';
import { timezone } from '@/utils/timezone.util';

const DEFAULT_SORTING = { id: 'createdAt', desc: true };
const DEFAULT_PAGING = { pageIndex: 0, pageSize: 100 };

export const Route = createFileRoute('/_main/_list/campaigns/$campaignId/edit-requests')({
  component: RouteComponent,
  params: {
    parse: ({ campaignId }) => ({ campaignId: vParse(vPipe(vString(), vTransform(Number), vNumber()), campaignId) }),
    stringify: ({ campaignId }) => ({ campaignId: String(campaignId) }),
  },
  validateSearch: vObject({
    pagination: vFallback(vNullish(vObject({ pageIndex: vNumber(), pageSize: vNumber() }), DEFAULT_PAGING), DEFAULT_PAGING),
    sorting: vFallback(vNullish(vObject({ id: vString(), desc: vBoolean() }), DEFAULT_SORTING), DEFAULT_SORTING),
  }),
  beforeLoad: async ({ search, params, context }) => {
    if (typeof (params.campaignId as unknown) !== 'number') {
      throw notFound({ routeId: '__root__' });
    }

    if (context.user && !context.can.campaignRequests.view()) {
      throw redirect({ to: '/' });
    }

    const detail = await context.queryClient.ensureQueryData(campaignRequestQueries.detail(params.campaignId));
    const campaignTimezone = detail.timezone ?? timezone.get();

    return {
      resource: Resource.campaignEditRequest as const,
      can: {
        [Resource.campaignEditRequest as const]: rPick(campaignEditRequestPolicies.bindContext({ user: context.user.original, organization: context.organization?.original }), [ 'create' ]),
      },
      queryOptions: {
        list: campaignEditRequestQueries.list(params.campaignId, {
          paging: search.pagination,
          sorting: search.sorting,
          campaignTimezone,
        }),
        campaignRequestDetail: campaignRequestQueries.detail(params.campaignId),
      },
    };
  },
  loader: async ({ context }) => {
    if (!context.user) {
      return undefined;
    }

    const campaignRequestDetailData = await context.queryClient.ensureQueryData(context.queryOptions.campaignRequestDetail);

    if (!campaignRequestDetailData) {
      throw redirect({ to: '/campaigns' });
    }

    await context.queryClient.ensureQueryData(context.queryOptions.list);

    return { detail: campaignRequestDetailData };
  },
  head: ({ loaderData }) => ({
    meta: [
      {
        title: `TrendMD | ${loaderData?.detail.campaignName ?? 'Campaign'} - Edit Requests`,
      },
    ],
  }),
  staticData: {
    list: {
      heading: 'Edit requests',
      slots: {
        topBarComponent: TopBar,
        toolbarLeftComponent: ToolbarLeftComponent,
        toolbarActionsComponent: ToolbarActionsComponent,
        bottomBarComponent: Pagination,
      },
    },
  },
});

function RouteComponent(): ReactElement {
  const routeContext = Route.useRouteContext();
  const routeParams = Route.useParams();
  const search = ListRoute.useSearch();
  const location = useLocation();
  const navigate = useNavigate();
  const newRecordContext = useContext(NewRecordContext);
  const queryClient = useQueryClient();

  const cancelMutation = useMutation(campaignEditRequestMutations.cancel);

  const [ columnsConfig ] = useLocalStorage(`table-columns-${routeContext.resource}`, campaignEditRequestTableOptions.initialConfig);
  const [ expandedRowIds, setExpandedRowIds ] = useState<Set<string>>(new Set());

  const query = useQuery(routeContext.queryOptions.list);
  const dataRows = query.data?.data ?? [];

  const toggleExpanded = (rowId: string): void => {
    setExpandedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  };

  const flatRows: EditRequestFlatRow[] = dataRows.flatMap((dataRow): EditRequestFlatRow[] => {
    const isExpanded = expandedRowIds.has(dataRow.meta.rowId);
    const parentRow: EditRequestParentRow = { ...dataRow, __rowKind: 'parent', isExpanded };
    const result: EditRequestFlatRow[] = [ parentRow ];

    if (dataRow.meta.isExpandable && isExpanded) {
      for (const group of dataRow.meta.changeGroups) {
        for (const item of group.items) {
          const subRow: EditRequestSubRow = {
            __rowKind: 'sub',
            parentRowId: dataRow.meta.rowId,
            itemKey: item.key,
            description: item.description,
          };
          result.push(subRow);
        }
      }
    }

    return result;
  });

  const config = campaignEditRequestTableOptions(flatRows, columnsConfig);
  const columns = config.getColumns();

  const rows: Array<EditRequestFlatRow | SkeletonRow> = query.isLoading ? config.getSkeletonRows() : flatRows;

  const [ hasMeasuredData, setHasMeasuredData ] = useState(false);
  if (!hasMeasuredData && rows.some((r) => !config.isSkeletonRow(r))) {
    setHasMeasuredData(true);
  }

  return (
    <TableOverlay
      isFullScreen
      rows={rows}
      isRowHighlighted={(row) => {
        if (config.isSkeletonRow(row) || isSubRow(row)) {
          return false;
        }
        return location.hash === `${routeContext.resource}-${row.meta.rowId}`;
      }}
      renderEmptyState={() => (
        !query.isLoading && <EmptyState><Heading>No data available</Heading></EmptyState>
      )}
      renderRowActions={(row) => {
        if (config.isSkeletonRow(row) || isSubRow(row)) {
          return null;
        }
        return (
          <TableRowSecondaryActions
            hash={`${routeContext.resource}-${row.meta.rowId}`}
            id={row.meta.rowId}>
            {row.original.status === CampaignRequestEditStatusEnum.IN_REVIEW
              ? ({ defaultChildren }) => (
                <>
                  <MenuItem onAction={() => navigate({ to: '/campaigns/$campaignId/edit/request', params: { campaignId: routeParams.campaignId }})}>
                    <EditStrokeIcon />
                    <Text>Edit</Text>
                  </MenuItem>
                  {defaultChildren}
                  <MenuItem
                    onAction={async () => {
                      await cancelMutation.mutateAsync({ id: row.original.id });
                      queryClient.invalidateQueries(campaignEditRequestQueries.all);
                      routeContext.toastQueue.add(
                        { message: 'Edit request cancelled.', variant: 'error' },
                        { timeout: TOAST_TIMEOUT },
                      );
                    }}>
                    <CloseStrokeIcon />
                    <Text>Cancel</Text>
                  </MenuItem>
                </>
              )
              : undefined}
          </TableRowSecondaryActions>
        );
      }}>
      <Virtualizer layout={TableLayout} layoutOptions={config.layoutOptions}>
        <ResizableTableContainer key={hasMeasuredData ? 'data' : 'loading'} {...stylex.props(resizableTableContainerStyles.base)}>
          <Table
            aria-label="Edit requests"
            selectionMode="none"
            sortDescriptor={{ column: search.sorting.id, direction: search.sorting.desc ? 'descending' : 'ascending' }}
            style={{ borderSpacing: 0 }}
            onSortChange={(sortDescriptor) => {
              const newId = sortDescriptor.column.toString();
              navigate({
                to: location.pathname,
                search: (prev) => ({
                  ...prev,
                  sorting: {
                    id: newId,
                    desc: newId === prev.sorting?.id ? !prev.sorting.desc : false,
                  },
                }),
              });
            }}>
            <TableHeader columns={columns}>
              {(column) => <Column {...column} isFullScreen />}
            </TableHeader>
            <TableBody<typeof rows[number]> dependencies={[ columns, query.isLoading, expandedRowIds, newRecordContext.newRecord ]}>
              {(item) => {
                const isSkeleton = config.isSkeletonRow(item);

                if (isSkeleton) {
                  return (
                    <Row columns={columns} id={item.id}>
                      {() => <Cell isFullScreen><Skeleton /></Cell>}
                    </Row>
                  );
                }

                if (isSubRow(item)) {
                  return (
                    <Row
                      columns={columns}
                      id={`${item.parentRowId}-sub-${item.itemKey}`}>
                      {(column) => {
                        const cellProps = config.getCell(column.id, item);
                        return cellProps ? <Cell {...cellProps} isFullScreen /> : <Cell isFullScreen />;
                      }}
                    </Row>
                  );
                }

                const parentRow = item as EditRequestParentRow;

                return (
                  <Row
                    columns={columns}
                    id={parentRow.meta.rowId}
                    variant={parentRow.meta.rowId === newRecordContext.newRecord ? 'new' : (parentRow.original.status === CampaignRequestEditStatusEnum.DECLINED || parentRow.original.status === CampaignRequestEditStatusEnum.CANCELLED) ? 'inactive' : undefined}
                    onAction={parentRow.meta.isExpandable ? () => toggleExpanded(parentRow.meta.rowId) : undefined}>
                    {(column) => {
                      const cellProps = config.getCell(column.id, parentRow);
                      if (!cellProps) {
                        return <Cell isFullScreen />;
                      }

                      if (column.id === columns[0]?.id && parentRow.meta.isExpandable) {
                        return (
                          <Cell {...cellProps} isFullScreen>
                            <div {...stylex.props(expandChevronStyles.wrapper)}>
                              <ChevronRightStrokeIcon
                                color="tertiary"
                                size={12}
                                slot={null!}
                                style={[ expandChevronStyles.icon, parentRow.isExpanded && expandChevronStyles.iconExpanded ]}
                              />
                              {cellProps.children as React.ReactNode}
                            </div>
                          </Cell>
                        );
                      }

                      return <Cell {...cellProps} isFullScreen />;
                    }}
                  </Row>
                );
              }}
            </TableBody>
          </Table>
        </ResizableTableContainer>
      </Virtualizer>
    </TableOverlay>
  );
}

const resizableTableContainerStyles = stylex.create({
  base: {
    height: '100%',
    overflow: 'auto',
    scrollbarWidth: 'none',
    width: '100%',
    willChange: 'transform',
  },
});

function ToolbarActionsComponent(): ReactElement | false {
  const isSmallScreen = useMediaQuery(breakpoints.small);

  return !isSmallScreen && <SecondaryActionsIconButton isTableActions />;
}

function ToolbarLeftComponent(): ReactElement | false {
  const isSmallScreen = useMediaQuery(breakpoints.small);

  return isSmallScreen && <SecondaryActionsIconButton isTableActions />;
}

function TopBar(): ReactElement {
  const routeParams = Route.useParams();
  const detailQuery = useQuery(campaignRequestQueries.detail(routeParams.campaignId));

  return (
    <Breadcrumbs>
      <Breadcrumb>
        <RouterLink to="/campaigns">Campaigns</RouterLink>
      </Breadcrumb>
      <Breadcrumb>
        <RouterLink params={routeParams} style={breadcrumbStyles.dynamic} to="/campaigns/$campaignId">
          {detailQuery.data?.original.campaignName}
        </RouterLink>
      </Breadcrumb>
      <Breadcrumb hidden>
        <RouterLink params={routeParams} to="/campaigns/$campaignId/edit-requests">Edit requests</RouterLink>
      </Breadcrumb>
    </Breadcrumbs>
  );
}

const breadcrumbStyles = stylex.create({
  dynamic: {
    display: 'block',
    maxWidth: 200,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});
